import { buildSaveReceiptingData } from './goodsReceiptingAsOrderedHelpers'
import { resolveAskForCredit } from './goodsReceiptingCreditHelpers'

// The production reason list is unchanged. Reasons select credit defaults;
// the entered fields and Ask for credit checkbox control the saved result.
export const RECEIPT_REASON_GUIDANCE = {
  Damaged:
    'Report damaged goods and describe the affected units in Notes. The quantity and price you enter will be saved as the receipt; this reason alone does not subtract damaged units.',
  'Quality Issue':
    'Describe the quality problem and affected units in Notes. OMS saves the received quantity and price you enter; selecting this reason does not automatically reduce them.',
  'Not Delivered':
    'This records zero received for the whole line when saved, even if a different quantity is entered. For a partial delivery, use Something Else, enter the actual received quantity and explain the missing units in Notes. Ask for credit stays off by default — an outstanding delivery is not automatically a financial credit until the invoice arrives.',
  'Something Else':
    'Describe the problem in Notes. OMS saves any quantity, price or product changes you enter. Ask for credit is selected by default.',
  Substitution:
    'Record the product actually supplied using the product details, quantity and price fields. Explain any disputed charge in Notes. Ask for credit is selected by default.',
  'Supplier / Invoice adjustment':
    'Use this to correct the recorded delivery price or details. It changes the receipt values you enter; credit is off by default. If the receipt is already correct and only the supplier charge is wrong, review the invoice difference and request credit there.',
  'Weighted item':
    'Record the actual delivered weight. OMS uses it as the received quantity. Credit is off by default.',
  'Read Only':
    'Credit is off by default. This reason does not itself lock the input fields; check the values before saving.',
}

function number(value) {
  return value == null || value === '' || !Number.isFinite(Number(value))
    ? null
    : Number(value)
}

export function buildReceiptImpact(
  record = {},
  drafts = [],
  lines = [],
  weights = [],
) {
  const supplier = String(record.supplier?._id || record.supplier || '')
  const selected = buildSaveReceiptingData({
    editedReceipting: drafts,
    weightedItems: weights,
    lineOrders: lines,
    supplierId: supplier,
    purchaseOrderNumber: record.purchaseOrderNumber,
  })
  return selected.map(draft => {
    const line =
      lines.find(row => String(row._id) === String(draft.order)) || {}
    const receipt = line.receipting || {}
    const orderedQty = number(
      draft.orderedQuantity ??
        receipt.orderedQuantity ??
        line.orderedQuantity ??
        line.quantity,
    )
    const receivedQty = number(
      draft.updatedQuantity ??
        draft.weight ??
        (draft.error === 'Not Delivered'
          ? 0
          : receipt.updatedQuantity ?? line.quantity ?? orderedQty),
    )
    // Expanded GR API returns prices in pounds; draft updatedPrice is pennies.
    // Use the same ordered unit price shown in the expanded row, without
    // guessing the units of legacy orderedPrice snapshots.
    const orderedPounds = number(line.price ?? line.product?.price)
    const orderedPrice =
      orderedPounds == null ? null : Math.round(orderedPounds * 100)
    const receivedPounds = number(
      receipt.updatedPrice ??
        line.brakesPrice ??
        line.price ??
        line.product?.price,
    )
    const receivedPrice =
      number(draft.updatedPrice) ??
      (receivedPounds == null ? null : Math.round(receivedPounds * 100))
    const orderedValue =
      orderedQty == null || orderedPrice == null
        ? null
        : Math.round(orderedQty * orderedPrice)
    const receivedValue =
      receivedQty == null || receivedPrice == null
        ? null
        : Math.round(receivedQty * receivedPrice)
    return {
      id: draft.order,
      title:
        draft.updatedTitle ||
        line.title ||
        line.product?.title ||
        draft.orderedTitle ||
        'Order line',
      reason: draft.error,
      notes: draft.notes,
      orderedQty,
      receivedQty,
      orderedPrice,
      receivedPrice,
      orderedValue,
      receivedValue,
      reductionPennies:
        orderedValue == null || receivedValue == null
          ? null
          : Math.max(0, orderedValue - receivedValue),
      askForCredit: resolveAskForCredit(draft),
    }
  })
}
